import { httpGet$GetCollectors } from "kreate-server/commands/GetCollectors/fetcher";
import {
  GetCollectors$Params,
  GetCollectors$Result,
} from "kreate-server/commands/GetCollectors/typing";
import useSWRInfinite from "swr/infinite";

// how many collectors per page
const LIMIT = 12;

type PageData = {
  result: GetCollectors$Result;
  nextOffset: number | undefined; // a.k.a. cursor
};

function last<T>(array?: T[]): T | undefined {
  return array ? array[array.length - 1] : undefined;
}

// TODO: @kien-kreate: move to @/modules/resources
export function useCollectorListInfinite(
  params: Omit<GetCollectors$Params, "offset" | "limit">
) {
  const swr = useSWRInfinite(
    (pageIndex: number, previousPageData: PageData | undefined) => {
      const thisPageParams: GetCollectors$Params | undefined =
        pageIndex === 0
          ? { ...params, offset: 0, limit: LIMIT }
          : previousPageData?.nextOffset != null
          ? { ...params, offset: previousPageData.nextOffset, limit: LIMIT }
          : undefined;
      return ["4cb1b62b-0439-4741-8ca9-f5e6e7bad248", thisPageParams];
    },
    async ([_, params]: [string, GetCollectors$Params]): Promise<PageData> => {
      const result = await httpGet$GetCollectors(
        "/api/v1/all-collectors",
        params
      );
      const nextOffset =
        params.limit != null &&
        params.offset != null &&
        result.collectors.length >= params.limit
          ? params.offset + result.collectors.length
          : undefined;
      return { result, nextOffset };
    }
  );

  const isEnded = !swr.isLoading && swr.size > 0 && !last(swr.data)?.nextOffset;

  const loadMore =
    !swr.isLoading && !isEnded ? () => swr.setSize((s) => s + 1) : undefined;

  const isEmpty =
    !swr.isLoading &&
    swr.size > 0 &&
    swr.data?.every((d) => !d.result.collectors.length);

  const totalCount = last(swr.data)?.result.count;

  const isLoadingMore =
    swr.isLoading || (!!swr.data && swr.size > swr.data.length);

  return {
    data: swr.data?.map((d) => d.result) satisfies
      | GetCollectors$Result[]
      | undefined,
    error: swr.error,
    isLoading: swr.isLoading,
    size: swr.size,
    loadMore,
    computed: { isEnded, isEmpty, totalCount, isLoadingMore },
    LIMIT, // just a quick and dirty way to expose this constant
  };
}
